import { useQuery } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";
import type { FrappeListResponse } from "@/lib/frappe-client";
import type { PricingRule } from "@/lib/erp/pricingRules";
import { pricingRuleQueryKeys } from "./pricingRuleQueryKeys";

export interface PricingRulesForItemParams {
  itemCode: string;
  pageIndex: number;
  pageSize: number;
}

/**
 * Hook for fetching Pricing Rules that apply to a specific item.
 * 
 * Uses a dedicated backend method that queries Pricing Rules with an EXISTS
 * subquery against the "Pricing Rule Item Code" child table.
 * 
 * This is necessary because Pricing Rules don't have a direct item_code field
 * on the parent doctype - items are stored in a child table.
 * 
 * P0 Requirements:
 * - Only queries when itemCode is valid (not empty, not "new")
 * - Uses consistent query keys via pricingRuleQueryKeys
 * - Includes staleTime to prevent refetch noise
 * - Maintains placeholderData for smooth pagination UX
 */
export function usePricingRulesForItemWithPagination(
  params: PricingRulesForItemParams
) {
  const { itemCode, pageIndex, pageSize } = params;

  return useQuery<FrappeListResponse<PricingRule>>({
    queryKey: pricingRuleQueryKeys.forItem(itemCode, pageIndex, pageSize),
    enabled: !!itemCode && itemCode !== "new",
    queryFn: async () => {
      return frappeMethod<FrappeListResponse<PricingRule>>(
        "zaviago_backend.api.pricing_rule.list_pricing_rules_for_item",
        {
          item_code: itemCode,
          page_index: pageIndex,
          page_size: pageSize,
        }
      );
    },
    staleTime: 30_000, // 30 seconds - prevents refetch noise when switching tabs
    placeholderData: (prev) => prev, // Keep previous data during pagination
  });
}

